'use client';

import type { AllManufacturersQueryResult } from '@app/sanity.types';
import RectangleAd from '@components/Advertisements/RectangleAd';
import Filter, { type AlphabetItem } from '@components/Maker/Filter';
import Makers from '@components/Maker/Makers';
import { useViewerPlan } from '@hooks/useViewerPlan';
import { groupMakersByFirstLetter } from '@utils/groupMakers';
import { Empty } from 'antd';
import { useMemo, useState } from 'react';

interface MakerPageClientProps {
  manufacturers: AllManufacturersQueryResult;
}

export const MakerPageClient = ({ manufacturers }: MakerPageClientProps) => {
  const { isSubscribed } = useViewerPlan();
  const [nameQuery, setNameQuery] = useState<string>('');

  const filteredManufacturers = useMemo(() => {
    if (nameQuery.length < 2) {
      return manufacturers;
    }

    const uppercaseQuery = nameQuery.toUpperCase();

    return manufacturers.filter((manufacturer) => manufacturer.name.toUpperCase().includes(uppercaseQuery));
  }, [manufacturers, nameQuery]);

  const makers = useMemo(() => {
    return new Map([...groupMakersByFirstLetter(filteredManufacturers).entries()].sort());
  }, [filteredManufacturers]);

  const alphabet = useMemo<AlphabetItem[]>(() => {
    return Array.from({ length: 26 }, (_, index) => {
      const char = String.fromCharCode(65 + index);

      return {
        char,
        enabled: makers.has(char),
      };
    });
  }, [makers]);

  const keys = useMemo(() => Array.from(makers.keys()), [makers]);

  return (
    <>
      <Filter setNameQuery={setNameQuery} alphabet={alphabet} />

      {!isSubscribed ? (
        <div style={{ padding: '0 24px', margin: '16px 0 24px 0' }}>
          <RectangleAd />
        </div>
      ) : null}

      {keys.map((item) => {
        const makerItems = makers.get(item) ?? [];

        return <Makers letter={item} key={item} items={makerItems} />;
      })}

      {!keys.length && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />}

      {!isSubscribed ? (
        <div style={{ padding: '0 24px', margin: '16px 0 24px 0' }}>
          <RectangleAd />
        </div>
      ) : null}
    </>
  );
};
